import { useAuth } from "@/contexts/AuthContext";
import { useAPI } from "@/hooks/useAPI";
import { useCanReadSubscriptions, usePermissionsV2Loaded } from "@/permissions/v2";
import { BillingStatusService } from "@/services/billingStatusService";
import type { BillingStatusResponse } from "@/types/billingStatus";
import { useMemo } from "react";
import useSWR from "swr";

const BILLING_STATUS_KEY = "/billing-status";

export const useBillingStatus = () => {
  const v2Api = useAPI("v2");
  const { firmContext } = useAuth();
  const billingStatusService = useMemo(() => new BillingStatusService(v2Api), [v2Api]);

  const isPermissionsLoaded = usePermissionsV2Loaded();
  const canViewBillingStatus = useCanReadSubscriptions(firmContext?.firmId);

  const { data, error, isLoading } = useSWR<BillingStatusResponse>(
    isPermissionsLoaded && canViewBillingStatus && firmContext?.firmId
      ? [BILLING_STATUS_KEY, firmContext.firmId]
      : null,
    async () => {
      if (!firmContext?.firmId) {
        throw new Error("Firm ID is required");
      }
      return await billingStatusService.getBillingStatus(firmContext.firmId);
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 60 * 60 * 1000, // 1 hour
      refreshInterval: 12 * 60 * 60 * 1000, // 12 hours
    }
  );

  return {
    billingStatus: data || null,
    isLoading,
    isError: error,
  };
};
